import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, share, throwError } from 'rxjs';
import { cacheDecorator } from '#core';
import { environment } from '#environments';
import { PagedList, UserDetails } from '#models';
import { BusinessUnitUser } from 'src/models/businessunit-user';
import { Pagination } from 'src/models/pagination';
import { User } from 'src/models/user';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _httpClient = inject(HttpClient);

  getSysAdmins(): Observable<User[]> {
    const url = environment.apiUrl + '/user/sys-admin';
    return this._httpClient.get<User[]>(url);
  }

  addSysAdminUser(email: string) {
    return this._httpClient.post(`${environment.apiUrl}/user/sys-admin`, {
      email,
    });
  }

  deleteSysAdminUser(id: number) {
    return this._httpClient.delete(`${environment.apiUrl}/user/sys-admin/${id}`);
  }

  addUser(email: string, businessUnitId: number) {
    return this._httpClient.post(`${environment.apiUrl}/user/${businessUnitId}`, {
      email,
      businessUnitId,
    });
  }

  requestAccess(email: string, businessUnitId: number): Observable<object> {
    return this._httpClient.post(`${environment.apiUrl}/user/${businessUnitId}/request-access`, {
      email,
      businessUnitId,
    });
  }

  approveAccess(email: string, businessUnitId: number): Observable<object> {
    return this._httpClient.post(`${environment.apiUrl}/user/${businessUnitId}/approve-access`, {
      email,
      businessUnitId,
    });
  }

  @cacheDecorator()
  me() {
    const result = this._httpClient
      .get<UserDetails>(environment.apiUrl + '/user/me')

      .pipe(
        share(),
        catchError(err => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            return of(null);
          }

          return throwError(() => err);
        })
      );

    return result;
  }

  getPaginatedUsersListByBusinessUnitId(
    { businessUnitId = 0, displayInactiveUsers = false },
    pagination: Pagination = {
      pageNumber: 1,
      pageSize: 10,
    }
  ) {
    return this._httpClient.post<PagedList<BusinessUnitUser>>(
      `${environment.apiUrl}/user/${businessUnitId}/paged-list`,
      {
        filter: {
          businessUnitId,
          displayInactiveUsers,
        },
        pagination,
      }
    );
  }

  disableBusinessUnitUser(businessUnitId: number, email: string) {
    return this._httpClient.post(`${environment.apiUrl}/user/${businessUnitId}/remove-access`, {
      businessUnitId,
      email,
    });
  }
  grantAccess(businessUnitId: number, email: string) {
    return this._httpClient.post(`${environment.apiUrl}/user/${businessUnitId}/approve-access`, {
      businessUnitId,
      email,
    });
  }
  searchUser(query: string) {
    return this._httpClient.get<
      {
        email: string;
        firstName: string;
        lastName: string;
      }[]
    >(`${environment.apiUrl}/user/search`, {
      params: {
        query,
      },
    });
  }

  getManager(email: string) {
    return this._httpClient.get<{ email: string; firstName: string; lastName: string }>(
      `${environment.apiUrl}/user/manager`,
      { params: { email } }
    );
  }
  toggleAdmin(businessUnitId: number, email: string, isAdmin: boolean) {
    const url = !isAdmin
      ? `${environment.apiUrl}/user/${businessUnitId}/set-bu-admin`
      : `${environment.apiUrl}/user/${businessUnitId}/unset-bu-admin`;

    return this._httpClient.post(url, {
      businessUnitId,
      email,
    });
  }
}
